import type { PapelEscritorio } from '@/types/escritorio/escritorioTypes';

import {
  DIAS_CARENCIA_ENTRADA,
  DIAS_PAUSA_APOS_RECUSAS,
  MOMENTOS_POR_ID,
  PRIORIDADE_GRUPO,
  RECUSAS_CONSECUTIVAS_PARA_PAUSA,
  TETO_DIAS_ENTRE_PESQUISAS,
} from '../constants/catalogoMomentos';
import type {
  ContextoResolucao,
  EventoElegivel,
  MomentoPesquisa,
  PesquisaExibida,
} from '../types/pesquisaSatisfacaoTypes';

const MS_POR_DIA = 86_400_000;

const PAPEIS_SEM_CRIACAO: PapelEscritorio[] = ['VIEWER'];

const ID_MOMENTO_ENTRADA = 'E1';

function diasEntre(inicioIso: string, fimIso: string): number {
  return (new Date(fimIso).getTime() - new Date(inicioIso).getTime()) / MS_POR_DIA;
}

function exibicaoMaisRecente(historico: PesquisaExibida[]): PesquisaExibida | null {
  return historico.reduce<PesquisaExibida | null>((maisRecente, atual) => {
    if (!maisRecente) return atual;
    return atual.exibidaEmIso > maisRecente.exibidaEmIso ? atual : maisRecente;
  }, null);
}

function ordenadoDoMaisRecente(historico: PesquisaExibida[]): PesquisaExibida[] {
  return [...historico].sort((a, b) => b.exibidaEmIso.localeCompare(a.exibidaEmIso));
}

export function dentroDoTeto(historico: PesquisaExibida[], agoraIso: string): boolean {
  const ultima = exibicaoMaisRecente(historico);
  if (!ultima) return false;
  return diasEntre(ultima.exibidaEmIso, agoraIso) < TETO_DIAS_ENTRE_PESQUISAS;
}

/**
 * Duas dispensas seguidas pausam a pesquisa por 90 dias (RN-07).
 *
 * Só `DISPENSADA` conta: expirar não é recusa — a pessoa pode simplesmente não ter
 * olhado para aquele canto —, e o cartão que ainda está aberto (`EXIBIDA`) não tem
 * desfecho nenhum. Por isso a contagem varre o histórico do mais recente para trás
 * ignorando esses dois estados: uma expiração no meio de duas dispensas não quebra
 * a sequência, e uma resposta quebra.
 */
export function pausadoPorRecusas(historico: PesquisaExibida[], agoraIso: string): boolean {
  const relevantes = ordenadoDoMaisRecente(historico).filter(
    (item) => item.desfecho === 'DISPENSADA' || item.desfecho === 'RESPONDIDA',
  );

  const recusasSeguidas: PesquisaExibida[] = [];
  for (const item of relevantes) {
    if (item.desfecho !== 'DISPENSADA') break;
    recusasSeguidas.push(item);
  }

  if (recusasSeguidas.length < RECUSAS_CONSECUTIVAS_PARA_PAUSA) return false;

  const ultimaRecusa = recusasSeguidas[0];
  if (!ultimaRecusa) return false;

  return diasEntre(ultimaRecusa.exibidaEmIso, agoraIso) < DIAS_PAUSA_APOS_RECUSAS;
}

export function jaConsumido(
  momento: MomentoPesquisa,
  historico: PesquisaExibida[],
  agoraIso: string,
): boolean {
  const exibicoes = historico.filter((item) => item.momentoId === momento.id);
  if (exibicoes.length === 0) return false;
  if (momento.recorrenciaDias === null) return true;

  const ultima = exibicaoMaisRecente(exibicoes);
  if (!ultima) return false;
  return diasEntre(ultima.exibidaEmIso, agoraIso) < momento.recorrenciaDias;
}

export function colisaoQueimada(momento: MomentoPesquisa, historico: PesquisaExibida[]): boolean {
  if (!momento.grupoColisao) return false;

  return historico.some((item) => {
    if (item.momentoId === momento.id) return false;
    return MOMENTOS_POR_ID[item.momentoId]?.grupoColisao === momento.grupoColisao;
  });
}

export function carenciaEntradaEmDias(papel: PapelEscritorio): number {
  return PAPEIS_SEM_CRIACAO.includes(papel) ? 0 : DIAS_CARENCIA_ENTRADA;
}

export function elegivelPorCarencia(momento: MomentoPesquisa, ctx: ContextoResolucao): boolean {
  if (momento.disparo !== 'CARENCIA') return false;
  if (!ctx.onboardingConcluidoEmIso) return false;
  if (ctx.houveCriacao) return false;

  return diasEntre(ctx.onboardingConcluidoEmIso, ctx.agoraIso) >= carenciaEntradaEmDias(ctx.papel);
}

function candidatosDeEventos(ctx: ContextoResolucao): EventoElegivel[] {
  const doCatalogo = ctx.eventos.filter((evento) => Boolean(MOMENTOS_POR_ID[evento.momentoId]));
  const momentoEntrada = MOMENTOS_POR_ID[ID_MOMENTO_ENTRADA];

  if (!momentoEntrada || !elegivelPorCarencia(momentoEntrada, ctx)) return doCatalogo;
  if (doCatalogo.some((evento) => evento.momentoId === ID_MOMENTO_ENTRADA)) return doCatalogo;

  return [
    ...doCatalogo,
    {
      momentoId: ID_MOMENTO_ENTRADA,
      ocorridoEmIso: ctx.onboardingConcluidoEmIso ?? ctx.agoraIso,
      recurso: null,
    },
  ];
}

export function resolverPesquisa(ctx: ContextoResolucao): MomentoPesquisa | null {
  // As duas regras de silêncio total vêm primeiro: nenhuma pesquisa aparece numa
  // sessão que já teve uma (RN-06), nem para quem recusou duas vezes seguidas
  // (RN-07). Nenhum momento é isento delas — `isentoDoTeto` vale só para a janela
  // semanal.
  if (ctx.jaExibiuNestaSessao) return null;
  if (pausadoPorRecusas(ctx.historico, ctx.agoraIso)) return null;

  const tetoAtivo = dentroDoTeto(ctx.historico, ctx.agoraIso);

  const elegiveis = candidatosDeEventos(ctx)
    .map((evento) => ({ evento, momento: MOMENTOS_POR_ID[evento.momentoId] as MomentoPesquisa }))
    .filter(({ momento }) => !jaConsumido(momento, ctx.historico, ctx.agoraIso))
    .filter(({ momento }) => !colisaoQueimada(momento, ctx.historico))
    .filter(({ momento }) => !tetoAtivo || momento.isentoDoTeto);

  if (elegiveis.length === 0) return null;

  const vencedor = elegiveis.sort((a, b) => {
    const porGrupo = PRIORIDADE_GRUPO[a.momento.grupo] - PRIORIDADE_GRUPO[b.momento.grupo];
    if (porGrupo !== 0) return porGrupo;

    const porTempo = a.evento.ocorridoEmIso.localeCompare(b.evento.ocorridoEmIso);
    if (porTempo !== 0) return porTempo;

    return a.momento.id.localeCompare(b.momento.id);
  })[0];

  return vencedor?.momento ?? null;
}
